// Polyomino shape definitions for BlastNova.
// Each shape is a 2D array of 0/1 cells with a neon color theme.
// Keep shapes compact (max 5x5) so they fit comfortably on an 8x8 board.

const COLORS = {
    cyan: { color: "#00f0ff", glow: "rgba(0, 240, 255, 0.55)" },
    magenta: { color: "#ff3ec9", glow: "rgba(255, 62, 201, 0.55)" },
    violet: { color: "#a26bff", glow: "rgba(162, 107, 255, 0.55)" },
    lime: { color: "#b6ff3c", glow: "rgba(182, 255, 60, 0.5)" },
    amber: { color: "#ffb547", glow: "rgba(255, 181, 71, 0.5)" },
    coral: { color: "#ff6b6b", glow: "rgba(255, 107, 107, 0.5)" },
    sky: { color: "#5cc8ff", glow: "rgba(92, 200, 255, 0.5)" },
};

// All shapes share a flat structure: { id, cells: number[][], theme: {color, glow} }
// Drag mechanics use this matrix directly (no rotation in MVP).
export const SHAPES = [
    // Single
    { id: "dot", cells: [[1]], theme: COLORS.cyan },

    // Dominoes
    { id: "h2", cells: [[1, 1]], theme: COLORS.cyan },
    { id: "v2", cells: [[1], [1]], theme: COLORS.cyan },

    // Triominoes
    { id: "h3", cells: [[1, 1, 1]], theme: COLORS.magenta },
    { id: "v3", cells: [[1], [1], [1]], theme: COLORS.magenta },
    {
        id: "l3a",
        cells: [
            [1, 0],
            [1, 1],
        ],
        theme: COLORS.lime,
    },
    {
        id: "l3b",
        cells: [
            [1, 1],
            [1, 0],
        ],
        theme: COLORS.lime,
    },
    {
        id: "l3c",
        cells: [
            [1, 1],
            [0, 1],
        ],
        theme: COLORS.lime,
    },
    {
        id: "l3d",
        cells: [
            [0, 1],
            [1, 1],
        ],
        theme: COLORS.lime,
    },

    // Tetrominoes
    { id: "h4", cells: [[1, 1, 1, 1]], theme: COLORS.violet },
    { id: "v4", cells: [[1], [1], [1], [1]], theme: COLORS.violet },
    {
        id: "sq2",
        cells: [
            [1, 1],
            [1, 1],
        ],
        theme: COLORS.amber,
    },
    {
        id: "l4a",
        cells: [
            [1, 0],
            [1, 0],
            [1, 1],
        ],
        theme: COLORS.coral,
    },
    {
        id: "l4b",
        cells: [
            [0, 1],
            [0, 1],
            [1, 1],
        ],
        theme: COLORS.coral,
    },
    {
        id: "l4c",
        cells: [
            [1, 1],
            [1, 0],
            [1, 0],
        ],
        theme: COLORS.coral,
    },
    {
        id: "l4d",
        cells: [
            [1, 1],
            [0, 1],
            [0, 1],
        ],
        theme: COLORS.coral,
    },
    {
        id: "ta",
        cells: [
            [1, 1, 1],
            [0, 1, 0],
        ],
        theme: COLORS.sky,
    },
    {
        id: "tb",
        cells: [
            [0, 1, 0],
            [1, 1, 1],
        ],
        theme: COLORS.sky,
    },

    // Pentominoes & big shapes
    { id: "h5", cells: [[1, 1, 1, 1, 1]], theme: COLORS.cyan },
    { id: "v5", cells: [[1], [1], [1], [1], [1]], theme: COLORS.cyan },
    {
        id: "sq3",
        cells: [
            [1, 1, 1],
            [1, 1, 1],
            [1, 1, 1],
        ],
        theme: COLORS.magenta,
    },
    {
        id: "Lbig",
        cells: [
            [1, 0, 0],
            [1, 0, 0],
            [1, 1, 1],
        ],
        theme: COLORS.violet,
    },
    {
        id: "Lbig2",
        cells: [
            [0, 0, 1],
            [0, 0, 1],
            [1, 1, 1],
        ],
        theme: COLORS.violet,
    },
    {
        id: "Lbig3",
        cells: [
            [1, 1, 1],
            [1, 0, 0],
            [1, 0, 0],
        ],
        theme: COLORS.violet,
    },
    {
        id: "Lbig4",
        cells: [
            [1, 1, 1],
            [0, 0, 1],
            [0, 0, 1],
        ],
        theme: COLORS.violet,
    },
];

/** @param {() => number} rng returns values in [0, 1) */
export function randomShapeFrom(rng = Math.random) {
    const s = SHAPES[Math.floor(rng() * SHAPES.length)];
    // Weighted slight bias towards medium-sized pieces handled by larger pool
    return { ...s, uid: rng().toString(36).slice(2, 10) };
}

export function randomShape() {
    return randomShapeFrom(Math.random);
}

export function shapeSize(cells) {
    return cells.reduce(
        (sum, row) => sum + row.reduce((a, b) => a + b, 0),
        0
    );
}

export function shapeDims(cells) {
    return { rows: cells.length, cols: cells[0].length };
}
